import { useState } from 'react';

import { Button } from '@mui/material';

import { MasterNameAutocomplete, NoResultItem } from '@/components';
import { IMasterName, MasterNameType } from '@/models';
import {
  formatPersonName,
  getMasterNameSecondaryInfo,
} from '@/utils';

import { ReporterDetailsModal, ReporterModalTab } from './ReporterDetailsModal';
import { ReporterItem } from './ReporterItem';

export interface PrimaryReporterFormProps {
  masterName: IMasterName | undefined;
  onChange: (newMasterName?: IMasterName) => Promise<void>;
  readOnly?: boolean;
}

export const PrimaryReporterForm = ({
  masterName,
  onChange,
  readOnly,
}: PrimaryReporterFormProps) => {
  const [reporterModalState, setReporterModalState] = useState<{
    open: boolean;
    mode: 'add' | 'update';
    initial?: IMasterName;
    defaultTab: ReporterModalTab;
  }>({
    open: false,
    mode: 'add',
    defaultTab: 'person',
  });
  return (
    <>
      {!masterName ? (
        <MasterNameAutocomplete
          includeOrganizations
          showFlags={false}
          disabled={readOnly}
          onChange={(v) => {
            onChange(v);
          }}
          addNewAction={() =>
            setReporterModalState({
              open: true,
              mode: 'add',
              defaultTab: 'person',
            })
          }
          noResults={
            <NoResultItem
              flexDirection="row"
              alignItems="center"
              sx={{ ml: -1 }}
            >
              <Button
                onClick={() =>
                  setReporterModalState({
                    open: true,
                    mode: 'add',
                    defaultTab: 'person',
                  })
                }
                variant="text"
                color="error"
              >
                + Add new
              </Button>
            </NoResultItem>
          }
        />
      ) : (
        <ReporterItem
          name={formatPersonName(masterName)}
          secondaryInfo={getMasterNameSecondaryInfo(masterName)}
          type={masterName.type}
          isEmployee={'isEmployee' in masterName && masterName.isEmployee}
          onEdit={
            readOnly
              ? undefined
              : () =>
                  setReporterModalState({
                    open: true,
                    mode: 'update',
                    initial: masterName,
                    defaultTab:
                      masterName.type === MasterNameType.ORGANIZATION
                        ? 'organization'
                        : 'person',
                  })
          }
          onRemove={readOnly ? undefined : () => onChange()}
        />
      )}
      <ReporterDetailsModal
        open={reporterModalState.open}
        mode={reporterModalState.mode}
        defaultTab={reporterModalState.defaultTab}
        initialValue={reporterModalState.initial}
        onSave={onChange}
        onClose={() =>
          setReporterModalState({
            open: false,
            mode: 'add',
            defaultTab: 'person',
            initial: undefined,
          })
        }
      />
    </>
  );
};
